/*
  This code defines a ModalPane class that represents different modal panes
   in the Self guided tour booking user interface.
  It provides the methods for creating and retrieving instances of the object.
*/
export default class ModalStep {
  constructor(name) {
    this.name = name;
  }

  /**
   * This is a static getter function that returns an instance of the "DateTimeSelection" modal pane.
   * This pane is used for selecting a date and time in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'dateTimeSelection'`.
   */
  static get DateTimeSelection() {
    return this.getInstance('dateTimeSelection');
  }

  /**
   * This is a static getter function that returns an instance of the "TourConfiguration" modal pane.
   * This pane is used for configuring the tour homes in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'tourConfiguration'`.
   */
  static get TourConfiguration() {
    return this.getInstance('tourConfiguration');
  }

  /**
   * This is a static getter function that returns an instance of the "TermsAndConditions" modal pane.
   * This pane is used for displaying the terms and conditions in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'termsAndConditions'`.
   */
  static get TermsAndConditions() {
    return this.getInstance('termsAndConditions');
  }

  /**
   * This is a static getter function that returns an instance of the "Confirmation" modal pane.
   * This pane is used for displaying the confirmation step in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'confirmation'`.
   */
  static get Confirmation() {
    return this.getInstance('confirmation');
  }

  /**
   * This is a static getter function that returns an instance of the "Questions" modal pane.
   * This pane is used for displaying the questions step in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'questions'`.
   */
  static get Questions() {
    return this.getInstance('questions');
  }

  /**
   * This is a static getter function that returns an instance of the "SignIn" modal pane.
   * This pane is used for displaying the sign in step in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'signIn'`.
   */
  static get SignIn() {
    return this.getInstance('signIn');
  }

  /**
   * This is a static getter function that returns an instance of the "PhoneVerification" modal pane.
   * This pane is used for displaying the phone verification step in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'phoneVerification'`.
   */
  static get PhoneVerification() {
    return this.getInstance('phoneVerification');
  }

  /**
   * This is a static getter function that returns an instance of the "IdentityVerification" modal pane.
   * This pane is used for displaying the identity verification step in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'identityVerification'`.
   */
  static get IdentityVerification() {
    return this.getInstance('identityVerification');
  }

  /**
   * This is a static getter function that returns an instance of the "CreditCardVerification" modal pane.
   * This pane is used for displaying the identity verification step in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'creditCardVerification'`.
   */
  static get CreditCardVerification() {
    return this.getInstance('creditCardVerification');
  }

  /**
   * This is a static getter function that returns an instance of the "IframeVerification" modal pane.
   * This pane is used for displaying the iframe verification step in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'iframeVerification'`.
   */
  static get IframeVerification() {
    return this.getInstance('iframeVerification');
  }

  /**
   * This is a static getter function that returns an instance of the "verificationInProgress" modal pane.
   * This pane is used for displaying the verification in progress step in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'verificationInProgress'`.
   */
  static get VerificationInProgress() {
    return this.getInstance('verificationInProgress');
  }

  /**
   * This is a static getter function that returns an instance of the "SignUp" modal pane.
   * This pane is used for displaying the sign up step in the self-guided tour booking interface.
   *
   * @returns {ModalStep} An instance of the class with the name `'signUp'`.
   */
  static get SignUp() {
    return this.getInstance('signUp');
  }

  /**
   * This is a static getter function that returns an instance of the "SpecSelection" modal pane.
   * This pane is used for displaying the specification selection step in the self-guided tour booking interface,
   * allowing the user to pick a specific plan or home to tour.
   *
   * @returns {ModalStep} An instance of the class with the name `'specSelection'`.
   */
  static get SpecSelection() {
    return this.getInstance('specSelection');
  }

  static fromKey(key) {
    const statusMap = {
      dateTimeSelection: ModalStep.DateTimeSelection,
      tourConfiguration: ModalStep.TourConfiguration,
      termsAndConditions: ModalStep.TermsAndConditions,
      questions: ModalStep.Questions,
      confirmation: ModalStep.Confirmation,
      signIn: ModalStep.SignIn,
      signUp: ModalStep.SignUp,
      iframeVerification: ModalStep.IframeVerification,
      VerificationInProgress: ModalStep.VerificationInProgress,
      specSelection: ModalStep.SpecSelection,
    };
    return statusMap[key];
  }

  /**
   * This function returns an instance of the ModalPane class based on a given key, creating a new
   * instance if one does not already exist.
   *
   * @param key - a unique identifier used to create and retrieve instances of the ModalStep class.
   * It is used to ensure that only one instance of the ModalPane class is created for each unique key value.
   *
   * @returns returns an instance of the `ModalPane` class with the specified `key`.
   * If an instance with the specified `key` already exists, it returns that instance instead of creating a new one.
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new ModalStep(key);
    }
    return this.instances[key];
  }

  toString() {
    return `${this.name}`;
  }
}

Object.defineProperty(ModalStep, 'instances', { value: {} });
